stimulusApp.register(
  "search--location-form",
  class extends Stimulus.Controller {
    static targets = [
      "pane",
      "searchInput",
      "fakeField",
      "fakeClearButton",
      "results",
      "loadingIcon",
      "destinationValue",
      "propertiesCount",
      "destinationIdInput",
      "destinationTypeInput",
      "locationIdInput",
      "regionIdInput",
      "landingIdInput",
      "locationsInputs",
      "recentsFrame",
      "suggestions",
    ]

    static outlets = ["search--idea", "search--idea-desktop"]

    static values = {
      endpoint: String,
      destination: { type: Object, default: {} },
      destinationName: String,
      submitPath: String,
      anywhereLabel: String,
      loadingLabel: { type: String, default: "..." },
      selectedIndex: { type: Number, default: -1 },
      autoFocusOnOpen: { type: Boolean, default: false },
      fakeFieldPlaceholder: String,
      title: String,
      name: String,
      locale: { type: String, default: "" },
      propertiesCountLabel: { type: Object, default: {} },
    }

    #ideaListShownFired = false
    #queryActive = false
    #debouncedLoad
    #onPaneScroll = null
    #unregisterBack = null

    connect() {
      if (!this.endpointValue) {
        console.error(
          "Endpoint value is required for search/location_form_controller, on:",
          this.element,
        )
      }

      this.#debouncedLoad = HB.debounce(this.#loadDestinations.bind(this), 250)
      this.trackSearch = HB.debounce(
        (query) => Activity.track_search(query),
        1000,
      )
      this.#setDestinationDirection(this.nameValue)

      if (this.#isOverlayPane()) {
        this.#onPaneScroll = () => this.#hideKeyboardOnScroll()
        this.paneTarget.addEventListener("scroll", this.#onPaneScroll, {
          capture: true,
          passive: true,
        })
        this.paneTarget.addEventListener("touchmove", this.#onPaneScroll, {
          capture: true,
          passive: true,
        })
      }
    }

    disconnect() {
      this.#debouncedLoad.cancel()
      this.trackSearch.cancel()
      this.#unregisterBack?.()
      if (this.#onPaneScroll) {
        this.paneTarget.removeEventListener("scroll", this.#onPaneScroll, {
          capture: true,
        })
        this.paneTarget.removeEventListener("touchmove", this.#onPaneScroll, {
          capture: true,
        })
        this.#onPaneScroll = null
      }
    }

    // iOS Safari auto-dismisses the soft keyboard when the page scrolls, but not
    // when an inner overflow container scrolls — which is all the mobile overlay
    // does. Blur the focused field so scrolling the results/suggestions hides the
    // keyboard. The connect() listener uses both scroll and touchmove because iOS
    // Safari never fires an element `scroll` on the inner overflow container while
    // the soft keyboard is up. https://tauportal.com/4090
    #hideKeyboardOnScroll() {
      if (document.activeElement !== this.searchInputTarget) return
      this.searchInputTarget.blur()
    }

    preload() {
      if (this.hasRecentsFrameTarget) {
        this.recentsFrameTarget.removeAttribute("loading")
      }
    }

    open() {
      this.preload()
      this.#resetSearchQuery()
      this.paneTarget.classList.add("place-search-expanded_opened")
      if (this.#isOverlayPane()) {
        this.paneTarget.classList.add("overlay_opened")
      }
      this.#setPaneMode("holidays")
      this.#resetIdeaSubView()
      this.#togglePaneEmpty()
      this.#syncFakeFieldValue()
      if (this.autoFocusOnOpenValue) {
        this.switchToSearchMode()
      }
      if (this.titleValue) {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ title: this.titleValue }),
        )
      }
    }

    close() {
      this.paneTarget.classList.remove("place-search-expanded_opened")
      if (this.#isOverlayPane()) {
        this.paneTarget.classList.remove("overlay_opened")
      }
      this.#setPaneMode("holidays")
      this.setIdeaLocationsActive(false)
    }

    setIdeaLocationsActive(active) {
      if (!this.hasPaneTarget) return
      this.paneTarget.classList.toggle(
        "place-search-expanded_idea-locations",
        active,
      )
    }

    #resetIdeaSubView() {
      this.setIdeaLocationsActive(false)
      if (this.hasSearchIdeaOutlet) {
        this.searchIdeaOutlet.reset()
      }
    }

    #focusInput() {
      if (!this.hasSearchInputTarget) return
      this.searchInputTarget.focus()
      const length = this.searchInputTarget.value.length
      this.searchInputTarget.setSelectionRange(length, length)
    }

    search(event) {
      const query = event.target.value.trim()
      this.#queryActive = query.length > 0
      this.#syncFakeFieldValue()
      this.#togglePaneEmpty()
      if (!query) {
        this.#debouncedLoad.cancel()
        this.clear({ keepEditing: true })
        return
      }
      this.trackSearch(query)
      this.#debouncedLoad(query)
    }

    clear(options = {}) {
      const keepEditing = options?.keepEditing === true
      this.#selectDestination(
        {
          name: this.anywhereLabelValue,
          locationId: null,
          regionId: null,
          landingId: null,
          destinationId: null,
          searchType: "location",
        },
        { closePane: false, keepEditing, skipTrack: true },
      )
      if (this.hasSearchInputTarget && !keepEditing) {
        this.searchInputTarget.value = ""
      }
      this.#clearResults()
      this.#togglePaneEmpty()
      if (keepEditing) {
        this.#setPaneMode("destination")
        this.#focusInput()
      } else {
        this.#setPaneMode("holidays")
        if (this.#isOverlayPane()) {
          this.#resetIdeaSubView()
          this.searchInputTarget?.blur?.()
        } else {
          this.#focusInput()
        }
      }
    }

    clearAndEdit() {
      if (!this.#isOverlayPane()) {
        this.clear()
        return
      }
      // iOS raises the soft keyboard only when focus() runs synchronously within
      // the tap and is NOT preceded by blur(). clear()'s overlay branch blurs, so
      // take the keepEditing (no-blur) path: empty the input + reset the idea
      // sub-view here, then let clear() reset the destination/results and focus
      // the field last.
      if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
      this.#resetIdeaSubView()
      this.clear({ keepEditing: true })
    }

    selectAllProperties() {
      Activity.track("filter", "change-category", "all")
      this.clear()
    }

    requestClose() {
      this.element.dispatchEvent(
        new CustomEvent("hb:search-form:close", { bubbles: true }),
      )
    }

    switchToSearchMode(event) {
      event?.preventDefault?.()
      this.#setPaneMode("destination")
      this.#focusInput()
    }

    switchToBrowseMode(event) {
      event?.preventDefault?.()
      this.#commitFirstResultIfPresent()
      this.#setPaneMode("holidays")
      this.searchInputTarget?.blur?.()
    }

    #isSearchMode() {
      return (
        this.hasPaneTarget &&
        this.paneTarget.classList.contains("location-mode_destination")
      )
    }

    // Registers a native-app Back handler while the full destination-search
    // input is showing (mobile overlay only): a hardware Back then steps back to
    // the placeholder field via switchToBrowseMode() instead of closing the
    // whole pane. Gated to the overlay so the desktop pop-out never registers.
    // See HB.nativeBack — front/app/assets/javascripts/front/lib/native_back.js.
    #notifyNativeSearchMode(active) {
      if (!this.#isOverlayPane()) return
      if (active) {
        this.#unregisterBack ||= HB.nativeBack.push(() =>
          this.switchToBrowseMode(),
        )
      } else {
        this.#unregisterBack?.()
        this.#unregisterBack = null
      }
    }

    setDestination(destination, syncInputValue = false) {
      if (!destination || typeof destination !== "object") return
      this.#selectDestination(destination, { syncInputValue })
    }

    #loadDestinations(query) {
      if (!this.hasResultsTarget) return
      setTimeout(() => this.#renderLoading(), 300)
      this.resultsTarget.setAttribute("src", this.#searchEndpoint(query))
    }

    #searchEndpoint(query) {
      const url = new URL(this.endpointValue, window.location.origin)
      url.searchParams.set("term", query)

      return url.pathname + url.search + url.hash
    }

    #renderLoading() {
      if (!this.hasResultsTarget) return
      if (!this.resultsTarget.hasAttribute("busy")) return

      this.#setSelectedIndex(-1)
      if (this.hasLoadingIconTarget) {
        this.resultsTarget.replaceChildren(
          this.loadingIconTarget.content.cloneNode(true),
        )
        return
      }
      this.resultsTarget.replaceChildren()
    }

    ignoreStaleSearchResults(event) {
      const loadedTerm = event.detail.newFrame?.querySelector(
        "ul.destination-search-results",
      )?.dataset.searchTerm
      if (loadedTerm !== this.searchInputTarget?.value.trim()) {
        event.detail.render = () => {
          // If we call preventDefault on the event, Turbo will think we're pausing
          // rendering and will not render anything else until we resume the event.
          // Instead, to skip rendering we replace render function with no-op.
        }
      }
    }

    activateSearchResults() {
      const loadedTerm = this.#loadedResultsTerm()
      if (loadedTerm !== this.searchInputTarget?.value.trim()) return
      if (this.#selectableResults().length) {
        this.#selectFirstResult()
      } else {
        this.#setSelectedIndex(-1)
        this.#trackEmptySearch(loadedTerm)
      }
    }

    #trackEmptySearch(query) {
      if (query === this.searchInputTarget?.value.trim()) {
        Activity.capture("empty destination search", { term: query })
      }
    }

    selectDestination(event) {
      const el =
        event.target.closest?.(
          "[data-location-name], [data-location-id], [data-destination-id]",
        ) || event.target
      const destination = {
        name: el.dataset.locationName || el.textContent.trim(),
        locationId: el.dataset.locationId || null,
        regionId: el.dataset.regionId || null,
        landingId: el.dataset.landingId || null,
        destinationId: el.dataset.destinationId || null,
        searchType: el.dataset.searchType || "location",
        searchIdeaLocationId: el.dataset.searchIdeaLocationId || null,
        propertiesCount: el.dataset.propertiesCount || null,
        submitPath: el.dataset.submitPath,
      }
      if (destination.searchIdeaLocationId) {
        this.#captureIdeaLocationSelected(el, destination)
      }
      if (this.hasSearchIdeaOutlet) {
        this.searchIdeaOutlet.notifyDestinationSelected()
      }
      this.#selectDestination(destination, {
        fromSearchResults: false,
        syncInputValue: true,
        ...(event.detail || {}),
      })
    }

    #captureIdeaLocationSelected(el, destination) {
      const ideaAncestor = el.closest("[data-search-idea-id], [data-idea-id]")
      const ideaId =
        ideaAncestor?.dataset?.searchIdeaId ||
        ideaAncestor?.dataset?.ideaId ||
        null
      const siblings = el.parentNode ? Array.from(el.parentNode.children) : []
      const position = siblings.indexOf(el)
      const session = this.#activeIdeaSession()
      Activity.capture("search-ideas location selected", {
        search_idea_location_id: destination.searchIdeaLocationId,
        idea_id: ideaId,
        location_id: destination.locationId,
        region_id: destination.regionId,
        destination_id: destination.destinationId,
        search_type: destination.searchType,
        position,
        surface: this.#isOverlayPane() ? "mobile-overlay" : "desktop-popout",
        idea_session_id: session?.sessionId || null,
      })
    }

    selectNext() {
      this.#moveSelection(1)
    }

    selectPrevious() {
      this.#moveSelection(-1)
    }

    selectResult(event) {
      const items = this.#selectableResults()
      const index = items.indexOf(event.currentTarget)
      if (index < 0) return
      this.#setSelectedIndex(index)
    }

    // Select the manually selected current result, for keyboard selection.
    pickCurrentResult() {
      const selected = this.#selectedResult()
      if (!selected) return
      this.selectDestination({
        target: selected,
        detail: { fromSearchResults: true, syncInputValue: false },
      })
    }

    // Select through an event fired directly on the result element, for selecting by clicking.
    pickResult(event) {
      this.selectDestination({
        target: event.currentTarget,
        detail: { fromSearchResults: true, syncInputValue: false },
      })
    }

    #selectDestination(destination, options = {}) {
      if (!options.skipTrack && destination) {
        Activity.capture("destination select", {
          query: this.searchInputTarget?.value.trim() || "",
          destination,
        })
      }
      this.#debouncedLoad?.cancel()
      this.#queryActive = false
      this.destinationValue = destination
      this.destinationNameValue = destination.name || this.anywhereLabelValue
      if (this.hasDestinationValueTarget) {
        this.destinationValueTarget.textContent =
          destination.name || this.anywhereLabelValue
      }
      this.submitPathValue = destination.submitPath || ""
      if (this.hasSearchInputTarget && options.syncInputValue === true) {
        this.searchInputTarget.value = destination.name || ""
      }
      if (this.hasLocationIdInputTarget) {
        this.locationIdInputTarget.value = destination.locationId || ""
      }
      if (this.hasLocationsInputsTarget) {
        this.locationsInputsTarget.innerHTML = ""
      }
      if (this.hasRegionIdInputTarget) {
        this.regionIdInputTarget.value = destination.regionId || ""
      }
      if (this.hasLandingIdInputTarget) {
        this.landingIdInputTarget.value = destination.landingId || ""
      }
      if (this.hasDestinationIdInputTarget) {
        this.destinationIdInputTarget.value = destination.destinationId || ""
      }
      if (this.hasDestinationTypeInputTarget) {
        this.destinationTypeInputTarget.value =
          destination.searchType === "property" ? "place" : ""
      }
      if (this.#isOverlayPane() && options.fromSearchResults) {
        this.searchInputTarget.value = ""
        this.#clearResults()
      }
      this.#togglePaneEmpty()
      if (this.#isOverlayPane() && options.fromSearchResults) {
        this.#setPaneMode("holidays")
        this.searchInputTarget?.blur?.()
      }
      if (!options.keepEditing) {
        this.#setPaneMode("holidays")
      }
      const closePane = options.closePane !== false
      if (closePane) {
        this.requestClose()
        if (destination?.searchIdeaLocationId) {
          this.#scrollToSearchForm()
        }
      }
      this.#loadSearchIdeaFilters(destination)
      this.#setDestinationDirection(destination.name)
      this.element.dispatchEvent(
        new CustomEvent("hb:search-form:destination-change", {
          bubbles: true,
        }),
      )
    }

    // On mobile the overlay close restores the pre-overlay scroll offset; on
    // desktop the form may be off-screen when the user picks an idea further
    // down the pane. Either way, bring the form back into view so the user
    // sees their destination land in it. Viewport.scrollTo gives a smooth
    // animation and a default -100 offset for breathing room above the form.
    #scrollToSearchForm() {
      const formContainer = this.element.closest(".place-search--container")
      if (!formContainer) return
      // When the desktop form is currently sticky (fixed), scrolling to the form
      // would cross the fixer threshold and collapse the panel, hiding the
      // just-loaded search-idea-filters. Scroll to the top of the page instead:
      // that drops below the threshold, fires fixer:released, and re-expands the
      // panel so the form + filters are visible. When not sticky, scroll to the
      // form as before.
      const target = document.body.classList.contains(
        "body_place-search-inner-fixed",
      )
        ? $(".header")
        : $(formContainer)
      requestAnimationFrame(() => Viewport.scrollTo(target))
    }

    #loadSearchIdeaFilters(destination) {
      const frame = document.getElementById("search_idea_filters")
      if (!frame) return
      const url = this.#searchIdeaFiltersUrl(destination)
      if (!url) {
        // Removing src aborts any in-flight frame fetch so a stale response
        // can't repopulate stale filter checkboxes after the destination
        // changes; replaceChildren() clears any already-rendered content.
        frame.removeAttribute("src")
        frame.replaceChildren()
        return
      }
      frame.setAttribute("src", url)
    }

    #searchIdeaFiltersUrl(destination) {
      if (destination.searchType === "property") return null
      if (!destination.locationId && !destination.landingId) return null

      const locale = (document.documentElement.lang || "en").toLowerCase()
      const params = new URLSearchParams()

      let path
      if (destination.locationId) {
        path = `/${locale}/destination_filters/${destination.locationId}`
        if (destination.searchIdeaLocationId) {
          params.set("search_location_id", destination.searchIdeaLocationId)
        }
      } else {
        path = `/${locale}/destination_filters/landing/${destination.landingId}`
      }

      const variant = new URLSearchParams(window.location.search).get("variant")
      if (variant) params.set("variant", variant)

      const query = params.toString()
      return query ? `${path}?${query}` : path
    }

    #applyDestinationEffects() {
      this.#setPropertiesCountVisibility(!this.#propertyIsSelected())
      this.#syncPropertiesCount()
    }

    #syncPropertiesCount() {
      if (!this.hasPropertiesCountTarget) return

      const count = this.#destinationPropertiesCount()
      this.propertiesCountTarget.textContent =
        !this.#propertyIsSelected() && count > 0
          ? this.#formatPropertiesCount(count)
          : ""
    }

    #destinationPropertiesCount() {
      const raw = this.destinationValue?.propertiesCount
      const parsed = raw == null ? NaN : Number(raw)
      return Number.isFinite(parsed) ? parsed : 0
    }

    #formatPropertiesCount(count) {
      const locale = this.localeValue || document.documentElement.lang || "en"
      const labels = this.propertiesCountLabelValue
      if (labels && Object.keys(labels).length && window.HB?.pluralize) {
        return window.HB.pluralize(count, labels, locale)
      }
      try {
        return count.toLocaleString(locale)
      } catch {
        return count.toLocaleString()
      }
    }

    #propertyIsSelected() {
      return this.destinationValue?.searchType === "property"
    }

    #setPropertiesCountVisibility(visible) {
      if (this.hasPropertiesCountTarget) {
        this.propertiesCountTarget.classList.toggle("hidden", !visible)
      }
    }

    destinationValueChanged() {
      this.#applyDestinationEffects()
      this.#syncFakeFieldValue()
    }

    #togglePaneEmpty() {
      if (!this.hasPaneTarget || !this.hasSearchInputTarget) return
      const isEmpty = !this.searchInputTarget.value.trim()
      const showSuggestions = !this.#queryActive
      this.paneTarget.classList.toggle("place-search-expanded_empty", isEmpty)
      if (this.hasRecentsFrameTarget) {
        this.recentsFrameTarget.classList.toggle("hidden", !showSuggestions)
      }
      if (this.hasSuggestionsTarget) {
        const wasHidden = this.suggestionsTarget.classList.contains("hidden")
        this.suggestionsTarget.classList.toggle("hidden", !showSuggestions)
        // Fire the list-shown impression only on the hidden→visible transition,
        // and only once per controller mount (prevents re-firing every time the
        // user toggles the search input or returns to the ideas list).
        if (
          wasHidden &&
          showSuggestions &&
          !this.#ideaListShownFired &&
          this.#hasSearchIdeas()
        ) {
          this.#ideaListShownFired = true
          Activity.capture("search-ideas list shown", {
            idea_count: this.#searchIdeasCount(),
            surface: this.#isOverlayPane()
              ? "mobile-overlay"
              : "desktop-popout",
          })
        }
      }
      if (!showSuggestions) {
        this.#ideaDesktopController()?.reset()
      }
    }

    #hasSearchIdeas() {
      return this.#searchIdeasCount() > 0
    }

    #searchIdeasCount() {
      const fromIdea = this.#ideaController()?.ideaCountValue
      if (typeof fromIdea === "number" && fromIdea > 0) return fromIdea
      const fromDesktop = this.#ideaDesktopController()?.ideaCountValue
      if (typeof fromDesktop === "number" && fromDesktop > 0) return fromDesktop
      return 0
    }

    #ideaController() {
      return this.hasSearchIdeaOutlet ? this.searchIdeaOutlet : null
    }

    #ideaDesktopController() {
      return this.hasSearchIdeaDesktopOutlet
        ? this.searchIdeaDesktopOutlet
        : null
    }

    #activeIdeaSession() {
      return (
        this.#ideaController()?.currentIdeaSession?.() ||
        this.#ideaDesktopController()?.currentIdeaSession?.() ||
        null
      )
    }

    #syncFakeFieldValue() {
      if (!this.hasFakeFieldTarget) return
      const selected = this.#locationSelected()
      const destinationName = String(this.destinationValue?.name || "").trim()
      const value =
        selected && destinationName
          ? destinationName
          : this.fakeFieldPlaceholderValue ||
            this.searchInputTarget?.placeholder ||
            ""
      this.fakeFieldTarget.textContent = value
      this.fakeFieldTarget.classList.toggle(
        "destination-search-input--field_placeholder",
        !selected,
      )
      if (this.hasFakeClearButtonTarget) {
        this.fakeClearButtonTarget.classList.toggle("hidden", !selected)
      }
    }

    #locationSelected(destination = this.destinationValue) {
      if (!destination) return false
      const hasLocationId = String(destination.locationId || "").trim() !== ""
      const hasLandingId = String(destination.landingId || "").trim() !== ""
      const hasDestinationId =
        String(destination.destinationId || "").trim() !== ""
      return hasLocationId || hasLandingId || hasDestinationId
    }

    #resetSearchQuery() {
      this.#debouncedLoad?.cancel()
      this.#queryActive = false
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.value = ""
      }
      this.#clearResults()
    }

    #clearResults() {
      if (this.hasResultsTarget) {
        this.resultsTarget.removeAttribute("src")
        this.resultsTarget.replaceChildren()
      }
      this.#setSelectedIndex(-1)
    }

    #loadedResultsTerm() {
      if (!this.hasResultsTarget) return null
      return this.resultsTarget.querySelector("ul.destination-search-results")
        ?.dataset.searchTerm
    }

    #moveSelection(delta) {
      const items = this.#selectableResults()
      if (!items.length) return
      const nextIndex =
        this.selectedIndexValue < 0
          ? 0
          : Math.min(
              Math.max(this.selectedIndexValue + delta, 0),
              items.length - 1,
            )
      this.#setSelectedIndex(nextIndex)
    }

    #selectFirstResult() {
      this.#setSelectedIndex(this.#selectableResults().length ? 0 : -1)
    }

    #selectableResults() {
      if (!this.hasResultsTarget) return []
      return Array.from(
        this.resultsTarget.querySelectorAll(
          ".destination-search-results--i[data-search-type]",
        ),
      )
    }

    #selectedResult() {
      const items = this.#selectableResults()
      if (!items.length) return null
      if (this.selectedIndexValue < 0) return null
      return items[this.selectedIndexValue] || null
    }

    #setSelectedIndex(index) {
      this.selectedIndexValue = index
      this.#syncSelectedClass()
    }

    #syncSelectedClass() {
      const items = this.#selectableResults()
      items.forEach((item, idx) => {
        item.classList.toggle(
          "destination-search-results--i_selected",
          idx === this.selectedIndexValue,
        )
      })
    }

    #isOverlayPane() {
      return this.hasPaneTarget && this.paneTarget.classList.contains("overlay")
    }

    #setPaneMode(mode) {
      if (!this.hasPaneTarget) return
      const destinationMode = mode === "destination"
      const wasDestinationMode = this.#isSearchMode()
      this.paneTarget.classList.toggle(
        "location-mode_destination",
        destinationMode,
      )
      this.paneTarget.classList.toggle(
        "location-mode_holidays",
        !destinationMode,
      )
      this.#togglePaneEmpty()
      if (destinationMode !== wasDestinationMode) {
        this.#notifyNativeSearchMode(destinationMode)
      }
    }

    #commitFirstResultIfPresent() {
      const selected = this.#selectedResult()
      if (!selected) return
      this.selectDestination({
        target: selected,
        detail: {
          closePane: false,
          fromSearchResults: true,
          syncInputValue: false,
        },
      })
    }

    #setDestinationDirection(name) {
      const dir = HB.getTextDirection(name)

      if (this.hasDestinationValueTarget) {
        this.destinationValueTarget.dir = dir
      }
      if (this.hasFakeFieldTarget) {
        this.fakeFieldTarget.dir = dir
      }
    }
  },
)
